import {
  Badge,
  DropdownButton,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import type { SearchFilterCondition, SearchFilters } from 'lib-search-types';
import orderBy from 'lodash/orderBy';
import {
  ChangeEvent,
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';

import {
  CustomFieldAttribute,
  customFieldsApi,
  CustomFieldSelectAttribute,
  CustomFieldTextAttribute,
} from 'apis/customFields';
import { ConditionFieldLayout } from 'app/views/layout/views/search/components/filters/components/condition/components/field/components/layout/conditionFieldLayout';

import classNames from 'classnames';
import { ContactsFilterAttributesSelect } from './components/select/contactsFilterAttributesSelect';
import { ContactsFilterAttributesText } from './components/text/contactsFilterAttributesText';
import styles from './contactsFilterAttributes.module.scss';

type ContactsFilterAttributesProps = {
  filters: SearchFilters<'users'>;
  onChangeFilters: (filters: SearchFilters<'users'>) => void;
};

export const ContactsFilterAttributes = memo(function ContactsFilterAttributes({
  filters,
  onChangeFilters,
}: ContactsFilterAttributesProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeAttribute, setActiveAttribute] =
    useState<CustomFieldAttribute | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const [readAttributes, { attributes, isLoading, isUninitialized }] =
    customFieldsApi.useLazyReadAttributesQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        attributes: data?.data.attributes ?? [],
      }),
    });

  // Fetch attributes the first time the dropdown opens.
  useUpdateEffect(() => {
    if (isOpen && isUninitialized) {
      const preferCacheValue = true;
      readAttributes({ entityId: 'contact' }, preferCacheValue);
    }
  }, [isOpen, isUninitialized, readAttributes]);

  const conditions = useMemo(
    () =>
      filters.filters.filter(
        (filter) =>
          'field' in filter && filter.field === 'contact_custom_attribute',
      ) as SearchFilterCondition<'users'>[],
    [filters.filters],
  );

  const handleChange = useCallback(
    (condition: SearchFilterCondition<'users'> | null, attributeId: string) => {
      const otherFilters = filters.filters.filter((filter) => {
        if (!('field' in filter)) return true;
        if (filter.field !== 'contact_custom_attribute') return true;
        return 'attributeId' in filter && filter.attributeId !== attributeId;
      });

      onChangeFilters({
        ...filters,
        filters: condition ? [...otherFilters, condition] : otherFilters,
      });
    },
    [filters, onChangeFilters],
  );

  const handleToggle = useCallback((newIsOpen: boolean) => {
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      setTimeout(() => inputRef.current?.focus());
    } else {
      setActiveAttribute(null);
      setSearchTerm('');
    }
  }, []);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    inputRef.current?.focus();
  }, []);

  const handleSelectAttribute = useCallback(
    ({ selectedItem }: SelectionListChangeEvent<CustomFieldAttribute>) => {
      setActiveAttribute(selectedItem.value);
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus());
    },
    [],
  );

  const handleBack = useCallback(() => {
    setActiveAttribute(null);
    setSearchTerm('');
    setTimeout(() => inputRef.current?.focus());
  }, []);

  /** Get the condition for a specific attribute by its ID. */
  const getConditionForAttribute = useCallback(
    (attributeId: string) =>
      conditions.find(
        (c) => 'attributeId' in c && c.attributeId === attributeId,
      ),
    [conditions],
  );

  const visibleAttributes = useMemo(() => {
    const fuse = new Fuse<CustomFieldAttribute>(attributes, {
      keys: ['label', 'identifier'],
      threshold: 0.2,
      distance: 200,
    });

    const filtered = debouncedSearchTerm
      ? fuse.search(debouncedSearchTerm).map(({ item }) => item)
      : [...attributes];

    return orderBy<CustomFieldAttribute>(filtered, 'is_deactivated', 'asc');
  }, [attributes, debouncedSearchTerm]);

  /** Handles rendering the list of custom attributes. */
  const renderAttributesList = () => {
    return (
      <>
        <ConditionFieldLayout.Row borderBottom>
          <InputText
            ref={inputRef}
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search attributes..."
            suffix={{
              type: 'custom',
              value: (
                <FlexBox alignItems="center" padding="0 100">
                  <IconButton
                    variant="tertiary"
                    icon={searchTerm ? 'close' : 'search'}
                    onClick={searchTerm ? handleClearSearch : undefined}
                  />
                </FlexBox>
              ),
              noWrap: true,
            }}
          />
        </ConditionFieldLayout.Row>

        <ConditionFieldLayout.Row ref={listWrapperRef} noPadding scrollable>
          <SelectionList
            isActive={isOpen}
            selectedValues={activeAttribute ? [activeAttribute] : undefined}
            onChange={handleSelectAttribute}
            equalityFn={(a, b) => a.identifier === b.identifier}
            defaultActiveItemIndex={0}
          >
            {visibleAttributes.map((attribute, index, list) => {
              const isFirstDeactivated =
                attribute.is_deactivated &&
                (index === 0 || !list[index - 1].is_deactivated);

              const attrCondition = getConditionForAttribute(attribute.id);
              const count = attrCondition
                ? attrCondition.values.length || 1
                : undefined;

              return (
                <Fragment key={attribute.identifier}>
                  {isFirstDeactivated && (
                    <SelectionList.Divider label="Deactivated" />
                  )}

                  <ConditionFieldLayout.Item
                    value={attribute}
                    label={attribute.label}
                    highlight={debouncedSearchTerm}
                    noFixedSelectionStateSpace
                    contentClassName={classNames(styles.itemContent, {
                      [styles.itemContentWithBadge]: !!count,
                    })}
                    textProps={{
                      color: attribute.is_deactivated ? 'tertiary' : undefined,
                    }}
                  >
                    {count && (
                      <Badge variant="secondary" size="small">
                        {count}
                      </Badge>
                    )}

                    <Icon name="angle-right" />
                  </ConditionFieldLayout.Item>
                </Fragment>
              );
            })}

            {isLoading && <ConditionFieldLayout.Loading />}

            {!isLoading && visibleAttributes.length === 0 && (
              <ConditionFieldLayout.Empty />
            )}
          </SelectionList>
        </ConditionFieldLayout.Row>
      </>
    );
  };

  /** Handles rendering of the active attribute according to its type. */
  const renderAttributeType = () => {
    if (!activeAttribute) return null;

    return (
      <>
        {activeAttribute.type === 'select' ? (
          <ContactsFilterAttributesSelect
            attribute={activeAttribute as CustomFieldSelectAttribute}
            condition={getConditionForAttribute(activeAttribute.id)}
            onChange={handleChange}
            onBack={handleBack}
          />
        ) : (
          <ContactsFilterAttributesText
            attribute={activeAttribute as CustomFieldTextAttribute}
            condition={getConditionForAttribute(activeAttribute.id)}
            onChange={handleChange}
            onBack={handleBack}
          />
        )}
      </>
    );
  };

  return (
    <DropdownButton
      show={isOpen}
      onToggle={handleToggle}
      buttonProps={{
        variant: 'tertiary',
        label: conditions.length || undefined,
      }}
      fixed
      dropdown={
        <ConditionFieldLayout>
          {activeAttribute ? renderAttributeType() : renderAttributesList()}
        </ConditionFieldLayout>
      }
    >
      Attributes
    </DropdownButton>
  );
});
